import { useEffect, useRef, useState } from 'react';

const ADSENSE_CLIENT = 'ca-pub-8090157202189708';

/**
 * Generic Google AdSense display unit. The site-ownership verification
 * script (client/public/index.html) already loads adsbygoogle.js
 * unconditionally on every page - this component only renders an actual
 * <ins> ad unit and pushes it once mounted.
 *
 * `slotId` is the per-placement AdSense ad unit ID from the AdSense
 * dashboard (Ads > By ad unit) - there isn't one configured for the
 * Property Guide yet, so this renders nothing until
 * REACT_APP_ADSENSE_BLOG_SLOT is set, rather than shipping a broken/empty
 * unit to production.
 */
export default function AdSenseSlot({ slotId, className = '', format = 'auto' }) {
  const insRef = useRef(null);
  const pushedRef = useRef(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!slotId || pushedRef.current || !insRef.current) {return;}
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch {
      // AdSense script blocked (ad blocker, consent not yet granted, etc.)
      // - fail silently, same as AdBanner's own network-failure handling.
      setCollapsed(true);
      return;
    }

    // AdSense reserves layout space for "auto"/responsive ins tags before it
    // knows whether the slot will fill, and only sets data-ad-status once it
    // resolves. If the account/site isn't approved to serve yet, that never
    // happens and the reserved space is left behind as a permanent empty
    // box - so collapse it ourselves if no fill signal ever arrives.
    const timer = setTimeout(() => {
      if (insRef.current && !insRef.current.getAttribute('data-ad-status')) {
        setCollapsed(true);
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [slotId]);

  if (!slotId || collapsed) {
    return null;
  }

  return (
    <ins
      ref={insRef}
      className={`adsbygoogle ${className}`.trim()}
      style={{ display: 'block' }}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={slotId}
      data-ad-format={format}
      data-full-width-responsive="true"
    />
  );
}
